/**
 * Variant Gallery Metafield Handler
 *
 * Extends the product page to support variant-specific media galleries from metafields.
 * Metafield: product.variant.metafields.custom.variant_gallery
 *
 * This file listens for variant changes and dynamically adds/removes media from the gallery
 * based on the selected variant's custom metafield data.
 */

if (!customElements.get('variant-gallery-handler')) {
  customElements.define(
    'variant-gallery-handler',
    class VariantGalleryHandler extends HTMLElement {
      constructor() {
        super();
        this.variantChangeUnsubscriber = undefined;
        this.currentVariantId = null;
        this.variantGalleryData = {};
        this.injectedMediaIds = new Set();
      }

      connectedCallback() {
        // Parse variant gallery data from the data attribute
        this.parseVariantGalleryData();

        // Subscribe to variant change events
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );

        // Handle initial variant on page load
        this.handleInitialVariant();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      parseVariantGalleryData() {
        // Get the data from the data attribute set by Liquid
        const dataElement = document.getElementById('variant-gallery-data');
        if (!dataElement) return;

        try {
          const rawData = dataElement.textContent;
          if (rawData) {
            this.variantGalleryData = JSON.parse(rawData);
          }
        } catch (error) {
          console.error('Error parsing variant gallery data:', error);
        }
      }

      handleInitialVariant() {
        // Get the initial variant ID from the product info element
        const productInfo = document.querySelector('product-info');
        if (!productInfo) return;

        const variantSelects = productInfo.querySelector('variant-selects [data-selected-variant]');
        if (variantSelects) {
          try {
            const variant = JSON.parse(variantSelects.innerHTML);
            if (variant && variant.id) {
              this.currentVariantId = variant.id;
              this.updateGalleryForVariant(variant);
            }
          } catch (error) {
            console.error('Error getting initial variant:', error);
          }
        }
      }

      handleVariantChange({ data: { variant } }) {
        if (!variant) {
          this.currentVariantId = null;
          this.removePreviouslyInjectedMedia();
          return;
        }

        if (variant.id === this.currentVariantId) return;

        this.currentVariantId = variant.id;
        this.updateGalleryForVariant(variant);
      }

      updateGalleryForVariant(variant) {
        if (!variant) return;

        const variantId = variant.id;

        // First, remove any previously injected media
        this.removePreviouslyInjectedMedia();

        // Ensure the variant's featured media is shown first
        const featuredMediaActivated = this.setFeaturedMediaActive(variant);

        // Get variant-specific gallery data
        const variantGallery = this.variantGalleryData[variantId];
        if (!variantGallery || !variantGallery.media || variantGallery.media.length === 0) {
          return;
        }

        // Add new media items to the gallery
        this.addMediaToGallery(variantGallery.media, variantId, {
          featuredMediaActivated,
          variant,
        });
      }

      removePreviouslyInjectedMedia() {
        const productInfo = document.querySelector('product-info');
        if (!productInfo) return;

        const mediaGallery = productInfo.querySelector('media-gallery ul');
        const mediaGalleryComponent = productInfo.querySelector('media-gallery');
        const viewerSlider = mediaGalleryComponent?.querySelector('slider-component');
        const thumbnailGallery = productInfo.querySelector(
          'media-gallery #Slider-Thumbnails-' + productInfo.dataset.section
        );
        const thumbnailSlider = thumbnailGallery?.closest('slider-component');

        if (!mediaGallery) return;

        // Remove main gallery items
        this.injectedMediaIds.forEach(mediaId => {
          const mediaItem = mediaGallery.querySelector(`[data-media-id="${mediaId}"]`);
          if (mediaItem) {
            mediaItem.remove();
          }

          // Remove corresponding thumbnail
          if (thumbnailGallery) {
            const thumbnailItem = thumbnailGallery.querySelector(`[data-target="${mediaId}"]`);
            if (thumbnailItem) {
              thumbnailItem.remove();
            }
          }
        });

        // Clear the set
        this.injectedMediaIds.clear();

        // Recalculate slider pagination after removal
        viewerSlider?.resetPages?.();
        thumbnailSlider?.resetPages?.();
      }

      addMediaToGallery(mediaList, variantId, { featuredMediaActivated, variant }) {
        const productInfo = document.querySelector('product-info');
        if (!productInfo) return;

        const sectionId = productInfo.dataset.section;
        const mediaGallery = productInfo.querySelector('media-gallery ul');
        const thumbnailGallery = productInfo.querySelector('media-gallery #Slider-Thumbnails-' + sectionId);

        const featuredMediaElement = variant
          ? this.getFeaturedMediaElement(productInfo, variant)
          : null;

        if (!mediaGallery) return;

        // Get the media gallery component for later use
        const mediaGalleryComponent = productInfo.querySelector('media-gallery');
        const viewerSlider = mediaGalleryComponent?.querySelector('slider-component');
        const thumbnailSlider = thumbnailGallery?.closest('slider-component');

        const existingThumbnailCount = thumbnailGallery
          ? thumbnailGallery.querySelectorAll('li[data-media-position]').length
          : 0;

        const featuredThumbnailElement = variant
          ? this.getFeaturedThumbnailElement(thumbnailGallery, sectionId, variant)
          : null;

        let previousInsertedGalleryItem = featuredMediaElement;
        let previousInsertedThumbnailItem = featuredThumbnailElement;

        let firstInjectedMediaId = null;

        mediaList.forEach((media, index) => {
          const mediaId = `${sectionId}-variant-${variantId}-${index}`;
          this.injectedMediaIds.add(mediaId);

          if (!firstInjectedMediaId) {
            firstInjectedMediaId = mediaId;
          }

          // Create main gallery item
          const galleryItem = this.createGalleryItem(media, mediaId, sectionId, existingThumbnailCount + index + 1);
          if (galleryItem) {
            if (previousInsertedGalleryItem && previousInsertedGalleryItem.parentNode === mediaGallery) {
              mediaGallery.insertBefore(galleryItem, previousInsertedGalleryItem.nextSibling);
              previousInsertedGalleryItem = galleryItem;
            } else {
              mediaGallery.appendChild(galleryItem);
              previousInsertedGalleryItem = galleryItem;
            }
          }

          // Create thumbnail item
          if (thumbnailGallery) {
            const positionIndex = existingThumbnailCount + index + 1;
            const thumbnailItem = this.createThumbnailItem(
              media,
              mediaId,
              sectionId,
              positionIndex,
              mediaGalleryComponent
            );
            if (thumbnailItem) {
              if (previousInsertedThumbnailItem && previousInsertedThumbnailItem.parentNode === thumbnailGallery) {
                thumbnailGallery.insertBefore(thumbnailItem, previousInsertedThumbnailItem.nextSibling);
                previousInsertedThumbnailItem = thumbnailItem;
              } else {
                thumbnailGallery.appendChild(thumbnailItem);
                previousInsertedThumbnailItem = thumbnailItem;
              }
            }
          }
        });

        if (!featuredMediaActivated && firstInjectedMediaId && mediaGalleryComponent?.setActiveMedia) {
          mediaGalleryComponent.setActiveMedia(firstInjectedMediaId, true);
        }

        // Update slider counter if it exists
        this.updateSliderCounter(productInfo);

        // Ensure slider components recalculate pagination with new items
        viewerSlider?.resetPages?.();
        thumbnailSlider?.resetPages?.();

        if (
          productInfo?.hasAttribute('data-zoom-on-hover') &&
          typeof enableZoomOnHover === 'function'
        ) {
          requestAnimationFrame(() => enableZoomOnHover(2));
        }
      }

      setFeaturedMediaActive(variant) {
        const productInfo = document.querySelector('product-info');
        if (!productInfo || !variant) return false;

        const sectionId = productInfo.dataset.section;
        const mediaGalleryComponent = productInfo.querySelector('media-gallery');
        if (!mediaGalleryComponent?.setActiveMedia) return false;

        const featuredMediaId = variant?.featured_media?.id || variant?.image?.id;
        if (!featuredMediaId) return false;

        const mediaId = `${sectionId}-${featuredMediaId}`;
        const featuredElement = mediaGalleryComponent.querySelector(`[data-media-id="${mediaId}"]`);
        if (!featuredElement) return false;

        mediaGalleryComponent.setActiveMedia(mediaId, true);
        return true;
      }

      getFeaturedMediaElement(productInfo, variant) {
        if (!productInfo || !variant) return null;

        const sectionId = productInfo.dataset.section;
        const featuredMediaId = variant?.featured_media?.id || variant?.image?.id;
        if (!featuredMediaId) return null;

        return productInfo.querySelector(
          `media-gallery [data-media-id="${sectionId}-${featuredMediaId}"]`
        );
      }

      getFeaturedThumbnailElement(thumbnailGallery, sectionId, variant) {
        if (!thumbnailGallery || !variant) return null;

        const featuredMediaId = variant?.featured_media?.id || variant?.image?.id;
        if (!featuredMediaId) return null;

        return thumbnailGallery.querySelector(
          `[data-target="${sectionId}-${featuredMediaId}"]`
        );
      }

      createGalleryItem(media, mediaId, sectionId, mediaPosition) {
        const li = document.createElement('li');
        li.id = `Slide-${mediaId}`;
        li.className = 'product__media-item grid__item slider__slide scroll-trigger animate--fade-in';
        li.setAttribute('data-media-id', mediaId);
        li.setAttribute('role', 'presentation');

        if (media.media_type === 'image') {
          li.innerHTML = this.createImageHTML(media, mediaId, sectionId, mediaPosition);
        } else if (media.media_type === 'video' || media.media_type === 'external_video') {
          li.classList.add('product__media-item--full');
          li.innerHTML = this.createVideoHTML(media, mediaId, sectionId);
        }

        return li;
      }

      createImageHTML(media, mediaId, sectionId, mediaPosition) {
        const sizes = '(min-width: 1200px) 715px, (min-width: 990px) 55vw, (min-width: 750px) calc(100vw - 10rem), 100vw';
        const srcset = this.generateSrcset(media.src, [246, 493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946]);
        const ratio = this.calculateRatio(media.width, media.height);
        const previewRatio = media.preview_image
          ? this.calculateRatio(media.preview_image.width, media.preview_image.height)
          : ratio;

        return `
          <div
            class="product-media-container media-type-image media-fit-contain global-media-settings gradient constrain-height"
            style="--ratio: ${ratio}; --preview-ratio: ${previewRatio};"
          >
            <modal-opener
              class="product__modal-opener product__modal-opener--image"
              data-modal="#ProductModal-${sectionId}"
            >
              <span class="product__media-icon motion-reduce quick-add-hidden product__media-icon--hover" aria-hidden="true">
                <span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-plus" viewBox="0 0 19 19"><path fill="currentColor" fill-rule="evenodd" d="M4.667 7.94a.5.5 0 0 1 .499-.501l5.534-.014a.5.5 0 1 1 .002 1l-5.534.014a.5.5 0 0 1-.5-.5" clip-rule="evenodd"></path><path fill="currentColor" fill-rule="evenodd" d="M7.926 4.665a.5.5 0 0 1 .501.498l.014 5.534a.5.5 0 1 1-1 .003l-.014-5.534a.5.5 0 0 1 .499-.501" clip-rule="evenodd"></path><path fill="currentColor" fill-rule="evenodd" d="M12.832 3.03a6.931 6.931 0 1 0-9.802 9.802 6.931 6.931 0 0 0 9.802-9.802M2.323 2.323a7.931 7.931 0 0 1 11.296 11.136l4.628 4.628a.5.5 0 0 1-.707.707l-4.662-4.662A7.932 7.932 0 0 1 2.323 2.323" clip-rule="evenodd"></path></svg></span>
              </span>

              <div class="loading__spinner hidden">
                <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
                  <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle>
                </svg>
              </div>
              <div class="product__media media media--transparent">
                <img
                  src="${media.src}"
                  alt="${media.alt || ''}"
                  class="image-magnify-hover"
                  loading="lazy"
                  sizes="${sizes}"
                  srcset="${srcset}"
                  width="${media.width || 1000}"
                  height="${media.height || 1000}"
                  data-media-id="${media.id || mediaId}"
                />
              </div>
              <button
                class="product__media-toggle quick-add-hidden product__media-zoom-hover"
                type="button"
                aria-haspopup="dialog"
                data-media-id="${media.id || mediaId}"
              >
                <span class="visually-hidden">${this.a11yString('openMedia', 'Open media [index] in modal', mediaPosition)}</span>
              </button>
            </modal-opener>
          </div>
        `;
      }

      // Translated labels from window.accessibilityStrings (layout/theme.liquid), English fallback
      a11yString(key, fallback, index) {
        const template = window.accessibilityStrings?.[key] || fallback;
        return template.replace('[index]', index ?? '');
      }

      calculateRatio(width, height) {
        const parsedWidth = Number(width);
        const parsedHeight = Number(height);
        if (!parsedWidth || !parsedHeight) return 1;
        const ratio = parsedWidth / parsedHeight;
        if (!Number.isFinite(ratio) || ratio <= 0) return 1;
        return Number.parseFloat(ratio.toFixed(4));
      }

      generateSrcset(baseUrl, widths) {
        // Generate srcset attribute with multiple image sizes
        if (!baseUrl) return '';

        return widths.map(width => {
          // Check if URL already has parameters
          const separator = baseUrl.includes('?') ? '&' : '?';
          return `${baseUrl}${separator}width=${width} ${width}w`;
        }).join(', ');
      }

      createVideoHTML(media, mediaId, sectionId) {
        // For videos, create a deferred media element
        if (media.host && (media.host === 'youtube' || media.host === 'vimeo')) {
          return `
            <deferred-media class="deferred-media media" data-media-id="${mediaId}">
              <button
                id="Deferred-Poster-Modal-${mediaId}"
                class="deferred-media__poster"
                type="button"
                aria-label="${this.a11yString('playVideo', 'Play video')}"
              >
                <img
                  src="${media.preview_image?.src || media.src}"
                  alt="${media.alt || ''}"
                  loading="lazy"
                  width="${media.preview_image?.width || 1000}"
                  height="${media.preview_image?.height || 1000}"
                />
                <span class="deferred-media__poster-button">
                  <svg class="icon icon-play" aria-hidden="true" focusable="false">
                    <use href="#icon-play"></use>
                  </svg>
                </span>
              </button>
              <template>
                ${media.host === 'youtube' ?
                  `<iframe src="https://www.youtube.com/embed/${media.external_id}?enablejsapi=1" allow="autoplay; encrypted-media" allowfullscreen></iframe>` :
                  `<iframe src="https://player.vimeo.com/video/${media.external_id}" allow="autoplay; encrypted-media" allowfullscreen></iframe>`
                }
              </template>
            </deferred-media>
          `;
        }

        // For native videos
        return `
          <div class="product__media media">
            <video controls>
              <source src="${media.src}" type="video/mp4">
            </video>
          </div>
        `;
      }

      createThumbnailItem(media, mediaId, sectionId, mediaPosition, mediaGalleryComponent) {
        const li = document.createElement('li');
        li.id = `Slide-Thumbnails-${mediaId}`;
        li.className = 'thumbnail-list__item slider__slide';
        li.setAttribute('data-target', mediaId);
        li.setAttribute('data-media-position', mediaPosition);

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'thumbnail global-media-settings global-media-settings--no-shadow';
        button.setAttribute(
          'aria-label',
          media.media_type === 'image'
            ? this.a11yString('loadImage', 'Load image [index] in gallery view', mediaPosition)
            : this.a11yString('loadVideo', 'Play video [index] in gallery view', mediaPosition)
        );
        button.setAttribute('aria-controls', `GalleryViewer-${sectionId}`);

        // Add click event listener
        button.addEventListener('click', () => {
          if (mediaGalleryComponent && typeof mediaGalleryComponent.setActiveMedia === 'function') {
            mediaGalleryComponent.setActiveMedia(mediaId, false);
          }
        });

        // Add thumbnail image
        const img = document.createElement('img');
        const thumbnailSrc = media.preview_image?.src || media.src;
        img.src = thumbnailSrc;
        img.alt = media.alt || '';
        img.loading = 'lazy';
        img.width = 416;
        img.height = 416;

        // Add srcset for thumbnail
        const thumbnailSrcset = this.generateSrcset(thumbnailSrc, [54, 74, 104, 162, 208, 324, 416]);
        if (thumbnailSrcset) {
          img.srcset = thumbnailSrcset;
        }

        // Add sizes attribute to match Shopify's default
        img.sizes = '(min-width: 1200px) calc((715 - 4rem) / 4), (min-width: 990px) calc((65.0vw - 4rem) / 4), (min-width: 750px) calc((100vw - 15rem) / 8), calc((100vw - 8rem) / 3)';

        button.appendChild(img);

        // Add video/model badge if needed
        if (media.media_type === 'video' || media.media_type === 'external_video') {
          const badge = document.createElement('span');
          badge.className = 'thumbnail__badge';
          badge.setAttribute('aria-hidden', 'true');
          badge.innerHTML = '<span class="svg-wrapper"><svg class="icon icon-play" aria-hidden="true" focusable="false"><use href="#icon-play"></use></svg></span>';
          li.appendChild(badge);
        }

        li.appendChild(button);
        return li;
      }

      updateSliderCounter(productInfo) {
        const mediaGallery = productInfo.querySelector('media-gallery ul');
        if (!mediaGallery) return;

        const totalMedia = mediaGallery.querySelectorAll('li[data-media-id]').length;
        const counterElement = productInfo.querySelector('.slider-counter--total');

        if (counterElement) {
          counterElement.textContent = totalMedia;
        }
      }
    }
  );
}
